import { FormikHelpers, FormikProps } from "formik";
import React, { FC } from "react";

import { clearVatNo, validateCreateCompany } from "@/data/companies";
import { useToggle } from "@/hooks/Toggle";

import { useAskToJoinSubmit, useCreateCompanySubmit } from "../hooks";
import { CreateCompanyFormik, ICreateCompanyFormValues } from "./CreateCompanyForm";

interface ICompanyCreatorProps {
  onCompanyCreated?: (profileId: number) => void;
  onAskToJoinSent?: () => void;
  onCancel?: () => void;
  onPrivatePersonChange?: () => void;
}

export const CompanyCreator: FC<ICompanyCreatorProps> = ({
  onCompanyCreated,
  onCancel,
  onPrivatePersonChange,
  onAskToJoinSent,
}) => {
  const [showDetails, toggleShowDetails] = useToggle(false);
  const [showAskToJoin, toggleShowAskToJoin] = useToggle(false);
  const [showProfileInCompany, toggleShowProfileInCompany] = useToggle(false);

  const submitCompany = useCreateCompanySubmit();
  const handleSubmit = async (values: ICreateCompanyFormValues, actions: FormikHelpers<ICreateCompanyFormValues>) => {
    if (showDetails) {
      const submitReturn = await submitCompany(values, actions);

      if (submitReturn.result === "success") {
        onCompanyCreated?.(submitReturn.profileId);
      } else if (submitReturn.result === "ask-to-join") {
        toggleShowAskToJoin();
      }
    } else {
      const vatValidation = await validateCreateCompany({
        countryCode: values.countryCode!,
        vatNo: clearVatNo(values.countryCode!, values.vatNo),
        orgNo: values.orgNo,
      });
      if (vatValidation.exists) {
        toggleShowAskToJoin();
      } else if (!vatValidation.validVatNo) {
        actions.setFieldError("vatNo", "INVALID_VAT");
      } else if (!vatValidation.validOrgNo) {
        actions.setFieldError("orgNo", "INVALID_ORG");
      } else {
        toggleShowDetails();
      }
    }
  };

  const submitAskToJoin = useAskToJoinSubmit();
  const handleAskToJoin = async (values: ICreateCompanyFormValues, form: FormikProps<ICreateCompanyFormValues>) => {
    const { status: askToJoinResult } = await submitAskToJoin(values, form);
    if (askToJoinResult === "success") {
      onAskToJoinSent?.();
    } else if (askToJoinResult === "profile-in-company") {
      toggleShowProfileInCompany();
    }
  };

  const handleCancel = () => {
    if (showDetails) {
      toggleShowDetails();
    }
    if (showAskToJoin) {
      toggleShowAskToJoin();
    }
    if (showProfileInCompany) {
      toggleShowProfileInCompany();
    }

    if (!showDetails && !showAskToJoin) {
      onCancel?.();
    }
  };

  return (
    <CreateCompanyFormik
      showCancelButton={showDetails || showAskToJoin}
      onCancel={handleCancel}
      onOk={onCancel}
      onSubmit={handleSubmit}
      onPrivatePersonChange={onPrivatePersonChange}
      askToJoin={showAskToJoin}
      profileInCompany={showProfileInCompany}
      onAskToJoin={handleAskToJoin}
      showDetails={showDetails}
    />
  );
};
